import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

import {
    COMPANY_SIZE_DISPLAY_OPTIONS,
    CompanySizeDisplayOption,
    FLASHLEVEL_INTERLOCUTOR_OPTIONS,
    PersonalizationAnswers,
    PersonalizationOption,
    TEAM_SIZE_OPTIONS,
    emptyPersonalizationAnswers,
    isPersonalizationComplete,
} from '../../../model/ai-personalization.model';
import { JobFormFuseNoUserComponent } from '../../job-form-fuse-no-user/job-form-fuse-no-user.component';
import { isJobFormComplete } from '../ai-exam-onboarding-job-form.util';

/** Mode d'affichage du titre de l'écran. */
export type ProfessionalUniverseTitleMode = 'full' | 'personalizationOnly';

/**
 * Écran fusionné métier + contexte professionnel (maquette 6.html).
 * Les champs métier/secteur sont délégués à lib-job-form-fuse-no-user.
 *
 * Mode `personalizationOnly` (showJobSection=false) : sections interlocuteurs,
 * taille d'organisation et management uniquement — utilisé par LILATE step 3b.
 */
@Component({
    selector: 'lib-ai-exam-onboarding-professional-universe',
    standalone: true,
    imports: [CommonModule, TranslateModule, JobFormFuseNoUserComponent],
    templateUrl: './AI-exam-onboarding-professional-universe.component.html',
    styleUrls: ['./AI-exam-onboarding-professional-universe.component.scss'],
})
export class AIExamOnboardingProfessionalUniverseComponent implements OnInit, OnChanges {
    @Input() jobForm?: FormGroup;
    @Input() answers: PersonalizationAnswers = emptyPersonalizationAnswers();
    @Input() languageName = '';
    /** Affiche la section métier/secteur (FlashLevel step job). */
    @Input() showJobSection = true;
    /** Affiche le bouton Retour dans la barre d'actions. */
    @Input() showBackButton = true;
    /** Titre et sous-titre : écran complet ou personnalisation seule (LILATE 3b). */
    @Input() titleMode: ProfessionalUniverseTitleMode = 'full';
    @Output() answersChange = new EventEmitter<PersonalizationAnswers>();
    @Output() back = new EventEmitter<void>();
    @Output() continue = new EventEmitter<void>();

    private destroyRef = inject(DestroyRef);

    companySizeOptions: CompanySizeDisplayOption[] = COMPANY_SIZE_DISPLAY_OPTIONS;
    interlocutorOptions: PersonalizationOption[] = FLASHLEVEL_INTERLOCUTOR_OPTIONS;
    teamSizeOptions: PersonalizationOption[] = TEAM_SIZE_OPTIONS;

    canContinue = signal(false);

    get titleKey(): string {
        return this.titleMode === 'personalizationOnly'
            ? 'ai-exam-onboarding.personalization-universe.title'
            : 'ai-exam-onboarding.professional-universe.title';
    }

    get subtitleKey(): string {
        return this.titleMode === 'personalizationOnly'
            ? 'ai-exam-onboarding.personalization-universe.subtitle'
            : 'ai-exam-onboarding.professional-universe.subtitle';
    }

    ngOnInit(): void {
        this.updateCanContinue();

        if (this.showJobSection && this.jobForm) {
            this.jobForm.valueChanges
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => this.updateCanContinue());
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['answers'] || changes['showJobSection']) {
            this.updateCanContinue();
        }
    }

    selectCompanySize(value: string): void {
        const company_size = this.answers.company_size === value ? null : value;
        this.emitAnswers({ ...this.answers, company_size });
    }

    toggleInterlocutor(value: string): void {
        const current = this.answers.interlocutor_types;
        const interlocutor_types = current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
        this.emitAnswers({ ...this.answers, interlocutor_types });
    }

    selectIsManager(value: boolean): void {
        this.emitAnswers({
            ...this.answers,
            is_manager: value,
            team_size: value ? this.answers.team_size : null,
        });
    }

    selectTeamSize(value: string): void {
        const team_size = this.answers.team_size === value ? null : value;
        this.emitAnswers({ ...this.answers, team_size });
    }

    isInterlocutorSelected(value: string): boolean {
        return this.answers.interlocutor_types.includes(value);
    }

    private updateCanContinue(): void {
        const personalizationOk = isPersonalizationComplete(this.answers);
        if (!this.showJobSection) {
            this.canContinue.set(personalizationOk);
            return;
        }
        const jobOk = this.jobForm ? isJobFormComplete(this.jobForm) : false;
        this.canContinue.set(jobOk && personalizationOk);
    }

    private emitAnswers(answers: PersonalizationAnswers): void {
        this.answers = answers;
        this.answersChange.emit(answers);
        this.updateCanContinue();
    }
}
